import i18n from '@/utils/i18n';
import React, { useCallback } from 'react';
import { Analytics } from '@/utils/analytics';
import {
  APP_CONTAINER_ROUTES,
  CATALOG_ROUTES,
  ORDER_GUIDE_ROUTES,
} from '@/navigators/routes';
import {
  AppContainerStackParams,
  CatalogStackParams,
  OrderGuideStackParams,
} from '@/navigators/types';
import {
  BRAND_PAGE_COLLECTION_SOURCE,
  BRAND_PAGE_SOURCE,
} from '@/utils/brand-page';
import { BrandPageLinkFieldsFragment } from '@/types';
import { Button } from '@/components/button';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import {
  CompositeNavigationProp,
  useNavigation,
} from '@react-navigation/native';
import { FONTS, theme } from '@/styles';
import { gql } from '@apollo/client';
import { ItemListOption } from '@/contexts/item-context-types';
import { MultiStyleText } from '@/components/multi-style-text';
import { StackNavigationProp } from '@react-navigation/stack';
import { StyleSheet } from 'react-native';
import { useGetActiveTabRouteIndex } from '@/hooks/use-get-active-tab-route-index';
import { useLayoutContext } from '@/contexts/layout-context';
import { useUserContext } from '@/contexts/user-context';

const BRAND_PAGE_LOGO_SIZE = 24;

export const BRAND_PAGE_VARIANT_PACKS_VIEW_QUERY = gql`
  fragment BrandPageLinkFields on brand_pages {
    brand_name
    logo_image_url
    uuid
  }

  query BrandPage_VariantPacksView($variantPackUUID: uuid!) {
    brand_page_variant_packs(
      where: { variant_pack_uuid: { _eq: $variantPackUUID } }
    ) {
      brand_page {
        ...BrandPageLinkFields
      }
      uuid
    }
  }
`;

type Navigation = CompositeNavigationProp<
  StackNavigationProp<AppContainerStackParams, APP_CONTAINER_ROUTES.BrandPage>,
  CompositeNavigationProp<
    StackNavigationProp<CatalogStackParams, CATALOG_ROUTES.BrandPage>,
    StackNavigationProp<OrderGuideStackParams, ORDER_GUIDE_ROUTES.BrandPage>
  >
>;

interface Props {
  brandPageDetails: BrandPageLinkFieldsFragment;
  onNavigate: () => void;
  option: ItemListOption;
  source: string;
}

export const BrandPageLink: React.FC<Props> = ({
  brandPageDetails,
  onNavigate,
  option,
  source,
}) => {
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
  } = useUserContext();
  const { navigate } = useNavigation<Navigation>();
  const { isSmallLayout } = useLayoutContext();
  const activeTabRouteIndex = useGetActiveTabRouteIndex();

  const onBrandPageLinkPress = useCallback(() => {
    const withinBrandPage = [
      BRAND_PAGE_SOURCE,
      BRAND_PAGE_COLLECTION_SOURCE,
    ].includes(source);

    if (!withinBrandPage) {
      Analytics.track('brand_page_link.press', {
        brandPageUUID: brandPageDetails.uuid,
      });
      const stackRoute =
        activeTabRouteIndex === 0
          ? ORDER_GUIDE_ROUTES.BrandPage
          : CATALOG_ROUTES.BrandPage;

      const targetRoute = isSmallLayout
        ? APP_CONTAINER_ROUTES.BrandPage
        : stackRoute;

      navigate(targetRoute, {
        brandPageUUID: brandPageDetails.uuid,
      });
    }

    onNavigate();
  }, [
    activeTabRouteIndex,
    brandPageDetails,
    isSmallLayout,
    navigate,
    onNavigate,
    source,
  ]);

  return (
    <Button
      leftElement={
        brandPageDetails.logo_image_url ? (
          <CachedImage
            alt={brandPageDetails.brand_name ?? ''}
            contentFit="contain"
            source={{
              uri: brandPageDetails.logo_image_url,
            }}
            style={styles.brandLogoImage}
            testID="brand-page-link-logo-image"
          />
        ) : null
      }
      leftElementStyle={styles.brandLogoContainer}
      onPress={onBrandPageLinkPress}
      size="small"
      style={styles.brandPageLink}
      textStyle={styles.brandName}
      trackingData={{
        name: 'item_details.brand_page_link',
        properties: {
          brandPageUUID: brandPageDetails.uuid,
          restaurantUUID,
          source,
          supplierUUID,
          variantPackUUID: option.metadata!.variantPackUUID,
        },
      }}
      type="tertiary"
    >
      <MultiStyleText
        defaultStyle={FONTS.primary}
        emphasisStyle={FONTS.primaryBold}
      >
        {i18n.t('brand_link_text', { brandName: brandPageDetails.brand_name })}
      </MultiStyleText>
    </Button>
  );
};

const styles = StyleSheet.create({
  brandLogoContainer: {
    marginRight: 8,
  },
  brandLogoImage: {
    height: BRAND_PAGE_LOGO_SIZE,
    width: BRAND_PAGE_LOGO_SIZE,
  },
  brandName: {
    fontSize: theme.fontSizes.small,
    textDecorationLine: 'underline',
  },
  brandPageLink: {
    alignSelf: 'flex-start',
    borderWidth: 0,
    paddingHorizontal: 0,
  },
});
